// Android-приложение: ссылка «Скачать приложение на Android» и окно «Вышло обновление» внутри APK.
// Версию приложения сообщает ссылка запуска (?app=android&v=N), актуальную — сервер (/app/android).
import { get } from '@/api/client';
import {
  APK_URL,
  type AndroidAppInfo,
  type AndroidStore,
  PLAY_URL,
  RUSTORE_URL,
  androidAppStore,
  androidAppVersion,
  canOfferApk,
} from '@/lib/androidApp';
import { nativeApp, nativeBridge, openExternal } from '@/lib/native';
import { useGame } from '@/store/game';
import { Button, cx } from '@/ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LATER_KEY = 'sk_apk_update_later';
const SEEN_VERSION_KEY = 'sk_app_version_seen';

/** Где взять новую версию: магазин или файл открываются системным браузером. */
function openAppUpdate(url?: string): void {
  const store = androidAppStore();
  const target = store === 'play' ? PLAY_URL : store === 'rustore' ? RUSTORE_URL : (url ?? APK_URL);
  if (nativeApp) openExternal(target);
  else window.location.assign(target);
}

/** Подпись кнопки: с сайта файл скачивается, в магазине обновляет он сам. */
function updateButtonText(store: AndroidStore): string {
  return store === 'site' ? 'androidApp.updateDownload' : 'androidApp.updateNow';
}

/** Ссылка на скачивание APK (не показывается внутри приложения и на iOS). */
export function DownloadApkLink({ className }: { className?: string }) {
  const { t } = useTranslation();
  if (!canOfferApk()) return null;
  return (
    <a
      href={APK_URL}
      download
      className={
        className ??
        'flex items-center justify-center gap-2 text-sm text-gold-300 underline underline-offset-2'
      }
    >
      📲 {t('androidApp.download')}
    </a>
  );
}

/** Внутри приложения: если на сервере новее версия — окно с предложением обновить APK. */
export function AndroidUpdatePrompt() {
  const { t } = useTranslation();
  const current = androidAppVersion();
  const store = androidAppStore();
  const [info, setInfo] = useState<AndroidAppInfo | null>(null);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    if (!current) return;
    const check = () =>
      void get<AndroidAppInfo>(`/app/android?store=${store}`, { auth: false })
        .then(setInfo)
        .catch(() => undefined);
    check();
    const id = window.setInterval(check, 6 * 3_600_000);
    return () => window.clearInterval(id);
  }, [current, store]);
  // После установки новой версии игрок сразу видит, что обновление прошло
  useEffect(() => {
    if (!current) return;
    try {
      const seen = Number(localStorage.getItem(SEEN_VERSION_KEY));
      if (seen && current > seen) {
        const version = nativeBridge()?.versionName() ?? current;
        useGame.getState().toast(t('androidApp.updated', { version }), 'success');
      }
      localStorage.setItem(SEEN_VERSION_KEY, String(current));
    } catch {
      // нет хранилища — без сообщения
    }
  }, [current, t]);
  if (!current || !info?.available || !info.versionCode || info.versionCode <= current) return null;
  const forced = (info.minVersionCode ?? 0) > current;
  let later = false;
  try {
    later = sessionStorage.getItem(LATER_KEY) === String(info.versionCode);
  } catch {
    // нет хранилища — спрашиваем каждый раз
  }
  if (!forced && (hidden || later)) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 p-3 sm:items-center">
      <div className="w-full max-w-md rounded-2xl border-2 border-gold-500 bg-ink-900 p-4 shadow-2xl">
        <h2 className="font-display text-xl text-gold-300">🆕 {t('androidApp.updateTitle')}</h2>
        <p className="mt-1 text-sm text-parchment-100">
          {t('androidApp.updateText', { version: info.versionName ?? info.versionCode })}
        </p>
        {info.notes && <p className="mt-2 whitespace-pre-line text-sm text-parchment-200">{info.notes}</p>}
        <p className="mt-2 text-xs text-parchment-300">
          {t(
            store === 'play'
              ? 'androidApp.updateHintPlay'
              : store === 'rustore'
                ? 'androidApp.updateHintRustore'
                : 'androidApp.updateHint',
          )}
        </p>
        {forced && <p className="mt-2 text-sm text-blood-400">{t('androidApp.updateForced')}</p>}
        <div className="mt-3 flex gap-2">
          <Button className="flex-1" onClick={() => openAppUpdate(info.url)}>
            ⬇️ {t(updateButtonText(store))}
          </Button>
          {!forced && (
            <Button
              variant="ghost"
              onClick={() => {
                try {
                  sessionStorage.setItem(LATER_KEY, String(info.versionCode));
                } catch {
                  // не важно
                }
                setHidden(true);
              }}
            >
              {t('androidApp.later')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Настройки: какая версия приложения стоит и нужно ли скачивать новую. */
export function AppVersionStatus() {
  const { t } = useTranslation();
  const current = androidAppVersion();
  const store = androidAppStore();
  const [info, setInfo] = useState<AndroidAppInfo | null>(null);
  const [checking, setChecking] = useState(false);
  const check = useCallback(() => {
    setChecking(true);
    get<AndroidAppInfo>(`/app/android?store=${store}`, { auth: false })
      .then(setInfo)
      .catch(() => useGame.getState().toast(t('androidApp.checkFailed'), 'error'))
      .finally(() => setChecking(false));
  }, [store, t]);
  useEffect(() => {
    if (current) check();
  }, [current, check]);
  if (!current) return null;
  const newer = !!info?.available && (info.versionCode ?? 0) > current;
  const name = nativeBridge()?.versionName() ?? String(current);
  return (
    <fieldset>
      <legend className="font-semibold">{t('androidApp.title')}</legend>
      <div
        role="status"
        className={cx(
          'my-1 rounded-xl border-l-4 bg-ink-800 p-3',
          newer ? 'border-gold-400' : 'border-moss-400',
        )}
      >
        <p className="text-sm text-parchment-300">{t('androidApp.installed', { version: name })}</p>
        <p className={cx('mt-1 font-semibold', newer ? 'text-gold-300' : 'text-moss-400')}>
          {newer
            ? t('androidApp.newVersion', { version: info?.versionName ?? info?.versionCode })
            : !info
              ? t('androidApp.checking')
              : store === 'rustore' && !info.available
                ? t('androidApp.viaRustore')
                : t('androidApp.upToDate')}
        </p>
        {newer && <p className="mt-1 text-sm text-parchment-200">{t('androidApp.mustDownload')}</p>}
        {!newer && <p className="mt-1 text-sm text-parchment-300">{t('androidApp.gameUpdatesItself')}</p>}
        <div className="mt-2 flex flex-wrap gap-2">
          {(newer || store === 'rustore') && (
            <Button onClick={() => openAppUpdate(info?.url)}>⬇️ {t(updateButtonText(store))}</Button>
          )}
          {!newer && (
            <Button variant="secondary" disabled={checking} onClick={check}>
              🔄 {t('androidApp.check')}
            </Button>
          )}
        </div>
      </div>
    </fieldset>
  );
}

const BUNDLE_LATER_KEY = 'sk_bundle_update_later';
/** Сколько ждать после запуска: приложение скачивает свежий набор в фоне. */
const BUNDLE_CHECK_DELAY = 90_000;

/** Внутри приложения: на сервере набор игры новее того, что сейчас запущен, — подсказка перезапустить
 *  приложение (скачанный набор включается при следующем запуске). */
export function BundleUpdateHint() {
  const { t } = useTranslation();
  const bridge = nativeBridge();
  const [newer, setNewer] = useState<number | null>(null);
  useEffect(() => {
    if (!bridge) return;
    const check = () =>
      void get<{ version: number; minNative: number }>('/app/bundle', { auth: false })
        .then((m) => {
          const mine = Number(bridge.bundleVersion()) || 0;
          if (m.version > mine && m.minNative <= bridge.versionCode()) setNewer(m.version);
        })
        .catch(() => undefined);
    const first = window.setTimeout(check, BUNDLE_CHECK_DELAY);
    const id = window.setInterval(check, 15 * 60_000);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(id);
    };
  }, [bridge]);
  let later = false;
  try {
    later = sessionStorage.getItem(BUNDLE_LATER_KEY) === String(newer);
  } catch {
    // нет хранилища — показываем
  }
  if (!newer || later) return null;
  return (
    <div className="fixed inset-x-2 bottom-[calc(72px+env(safe-area-inset-bottom))] z-[90] rounded-xl border border-gold-500 bg-ink-900/95 p-3 text-sm shadow-2xl">
      <p className="font-semibold text-gold-300">🔄 {t('androidApp.bundleTitle')}</p>
      <p className="mt-1 text-parchment-200">{t('androidApp.bundleText')}</p>
      <div className="mt-2 flex justify-end">
        <Button
          variant="ghost"
          onClick={() => {
            try {
              sessionStorage.setItem(BUNDLE_LATER_KEY, String(newer));
            } catch {
              // не важно
            }
            setNewer(null);
          }}
        >
          {t('androidApp.later')}
        </Button>
      </div>
    </div>
  );
}
